/**
 * Compact confirmation shown after a successful charge, in place of the full-page
 * `WizardStepFinish`.
 *
 * Why a small dialog: at this point the flow is over. The full-page treatment gave a one-line
 * outcome the same weight as the plan and payment steps, so it read as another step still to be
 * worked through, and it left the user parked in the wizard with no obvious way back into the
 * product.
 *
 * Covers a first subscribe AND a plan/quantity change — the copy and the money row adapt to the
 * charge's `finishAction`. The one case still routed to the full page is an order-device coupon,
 * which carries a second decision ("Continue Later") that a single-action popup cannot express.
 *
 * WHERE THE MONEY FIGURES COME FROM: on a change that bills on the next invoice
 * (`increase` / `decreaseNotRefund`) every figure is read off `chargeResult.upcoming` — the same
 * Stripe upcoming invoice, through the same shared helpers, that `UpcomingChargeDialog` and both
 * `WizardStepFinish`es read. One document in, one derivation, so the rows cannot disagree with
 * each other or with what control shows for the same charge. On the paid-today path there is no
 * such invoice yet and the wizard's own client-side figures are used instead.
 *
 * This replaced a hand-rolled block that mixed the two sources — an invoice-derived headline
 * above a client-derived closing row — which is what produced #2160 (two different next-invoice
 * amounts in one dialog) and #2162 (a Subtotal containing add-ons that nothing on screen named).
 *
 * WHAT IS DELIBERATELY NOT HERE: the full page's TABLE CHROME — the DESCRIPTION / QTY / AMOUNT
 * columns and the route back to the invoice. The line items themselves ARE here, as plain rows,
 * because a Subtotal a reader cannot account for is not a receipt. The full invoice lives
 * permanently on Payment History (reachable from the account menu and the Settings sidebar) and
 * in the emailed receipt, so repeating a route to it from an acknowledgement dialog only added a
 * second action competing with the one that dismisses it.
 */
import { useTranslation } from 'react-i18next';
import { Button, DialogDescription, DialogTitle } from '@scio3/ui';
import { useAuthStore } from '@/stores/auth-store';
import { useHardwareCta } from '@/features/screens/components/WelcomeScreen/useHardwareCta';
import { PRODUCT_IMAGE_BASE } from '../../components/order-device/product-order';
import { formatCurrencyAmount } from '../../utils/plan-helpers';
import { aggregateInvoiceLines, type InvoiceLine } from '../../utils/aggregate-invoice-lines';
import { formatDiscountLabel, sumDiscountAmounts } from '../../utils/format-coupon';
import { formatWithPattern } from '@/lib/format-date';

/**
 * Drops Stripe's trailing unit-price parenthetical from an invoice line description:
 * `5 × Standard Yearly (at $108.00 / year)` → `5 × Standard Yearly`.
 *
 * Two reasons, both introduced by moving the per-screen price onto the Screens row:
 *  - the PLAN line then states that price a second time, a few rows below the first;
 *  - at this dialog's width the full description wraps onto a second line, so a four-row
 *    breakdown becomes seven ragged ones.
 *
 * The quantity survives (`5 ×`, `4 ×`), so an add-on's own unit price — which the Screens row
 * does NOT carry — is still recoverable from its amount. The full itemisation with its own
 * columns lives on Payment History and in the emailed receipt.
 *
 * Deliberately narrow: it matches only a trailing `(at …)` group, so a product whose NAME
 * contains brackets keeps them, and a description in any other shape is returned untouched
 * rather than truncated on a guess.
 */
export function shortenInvoiceDescription(description?: string | null): string {
  return (description ?? '').replace(/\s*\(at\s[^)]*\)\s*$/i, '').trim();
}

/** Same four actions as WizardStepFinish's `isFinishAction`. */
export type ThankYouFinishAction = 'trial' | 'increase' | 'decrease' | 'decreaseNotRefund';

export type SubscribedChargeResult = {
  finishAction?: ThankYouFinishAction | string;
  planName?: string;
  billingCycle?: string;
  screens?: number;
  totalPaidToday?: number;
  /**
   * Account credit applied to this charge, already in DOLLARS (backend sends
   * `customer.balance / 100`). Rendered verbatim, sign included, so it reconciles with the
   * same figure on the full receipt and on Payment History.
   */
  credit?: number;
  email?: string;
  /** Pre-change snapshot, captured in `useSubscriptionWizard` before the post-charge refetch.
   *  Absent on a first subscribe, where there is nothing to compare against. */
  previousPlanName?: string;
  previousScreens?: number;
  previousBillingCycle?: string;
  /**
   * Stripe upcoming invoice — the SAME raw object `UpcomingChargeDialog` and both
   * `WizardStepFinish`es consume, so the field names and units match theirs exactly. Every
   * figure here is in CENTS, unlike the top-level dollar fields below.
   *
   * On an `increase` / `decreaseNotRefund` this is the authoritative source for the whole money
   * block: it is the invoice the customer will actually be charged, already carrying tax,
   * discount and balance. The top-level `subtotal` / `total` are CLIENT-side estimates
   * (`clientSubtotal`) that never see tax — see the note on `total`.
   */
  upcoming?: {
    lines?: { data?: InvoiceLine[] };
    subtotal?: number;
    total?: number;
    ending_balance?: number;
    starting_balance?: number;
    /** Backend pre-clamps to >= 0 (scio1 parity); `amount_due` is the raw Stripe field. */
    amount?: number;
    amount_due?: number;
    /**
     * When this invoice will be issued, Unix SECONDS. On an upcoming invoice that is the date of
     * the next charge, which is why the full-page `WizardStepFinish` dates its table from the
     * same field — both surfaces must name the same day for the same charge.
     */
    created?: number;
    taxs?: Array<{ name?: string; tax?: number }> | null;
    discount?: { coupon?: { name?: string; percent_off?: number; amount_off?: number } };
    total_discount_amounts?: Array<{ amount?: number }>;
  } | null;
  /**
   * Per-add-on rows (Video Wall, OptiSound Music, …) in DOLLARS, built by the wizard at
   * `useSubscriptionWizard:960` and carried in the charge result. Used on the paid-today path,
   * where there is no upcoming invoice to itemise from. Tiered add-ons arrive pre-formatted as
   * `{name} Add-on — {Tier}`, matching `WizardStepFinish`.
   */
  addonItems?: Array<{ name?: string; total?: number }> | null;
  /**
   * The money breakdown, in DOLLARS (unlike `upcoming.total`, which is cents). Optional
   * throughout: the first-subscribe path stashes its receipt across a document swap and does not
   * always carry every field, so each row below renders only when its own value is present.
   */
  subtotal?: number;
  /** What the subscription bills from the next period on. */
  total?: number;
  /** Per-jurisdiction tax, in CENTS, `name` pre-formatted — e.g. `Sales Tax - Texas(8.25%)`. */
  taxs?: Array<{ name?: string; tax?: number }> | null;
  /**
   * What ONE screen costs per billing period, in DOLLARS (`useSubscriptionWizard:2074`). Rendered
   * beside the Screens row so the count and its unit price sit together, which is what lets the
   * single-product line item be dropped — see `itemRows`.
   */
  pricePerScreen?: number;
  /**
   * The card the charge landed on. NOT rendered here any more — this dialog confirms what the
   * subscription now costs, and which card it went on is neither in question nor changeable from
   * a confirmation; it stays on Payment History and the emailed receipt. Kept on the type because
   * the wizard still populates it and the full-page `WizardStepFinish` still prints it.
   */
  cardBrand?: string;
  cardLast4?: string;
  /**
   * A free-device coupon this purchase earned. BOTH are needed to build the order-device link,
   * which is why the CTA tests the pair rather than the flag alone.
   */
  orderDeviceCoupon?: string | number;
  orderDeviceCouponNumber?: string | number;
};

type Props = {
  chargeResult: SubscribedChargeResult | null | undefined;
  currency: string;
  /**
   * Dismisses the dialog. On a first subscribe the wizard's close handler additionally performs
   * the hard navigation to Screens, which is why the button says "Done" rather than naming a
   * destination — where you land after finishing is the app's business, not a second choice to
   * make here.
   */
};

/**
 * Whether this receipt will render the hardware promo column.
 *
 * Exported because the DIALOG has to know: the promo is a 224px aside, so the dialog is `max-w-2xl`
 * when it shows and `max-w-md` when it does not — a receipt with three rows in a 672px shell is
 * mostly empty space. Deriving both from one predicate is what stops the width and the content
 * disagreeing, which is exactly the bug you get from copying the condition into the dialog: change
 * the gate here, and there a wide-but-empty dialog quietly survives.
 *
 * `finishAction` defaults to 'trial' for the same reason it does inside the component — an unknown
 * action must not be treated as a plan change when it may have been a first subscribe.
 */
export function hasHardwarePromo(
  chargeResult: SubscribedChargeResult | null | undefined,
  isWhiteLabel: boolean,
): boolean {
  return !isWhiteLabel && (chargeResult?.finishAction ?? 'trial') === 'trial';
}

export function SubscribedThankYou({ chargeResult, currency }: Props) {
  const { t } = useTranslation();
  const { openHardwareCta } = useHardwareCta();
  /**
   * `isWhiteLabel`, not `removeOptiSignsBranding`: hardware ordering sits on the same flag as
   * every other Order Devices entry point (user menu, Settings sidebar, Screens toolbar, Coupons)
   * — see the white-label rules. A white-label portal must not send its customers to
   * shop.optisigns.com, which is where `useHardwareCta` routes anyone who is not a root admin in
   * the default team.
   */
  const isWhiteLabel = !!useAuthStore((state) => state.me)?.brandingData?.isWhiteLabel;

  /** Defaults to 'trial' when absent, matching WizardStepFinish. An unknown action must not
   *  tell someone their plan changed when it may have been their first subscribe. */
  const finishAction = chargeResult?.finishAction ?? 'trial';
  const isFirstSubscribe = finishAction === 'trial';
  /** These two bill on the NEXT invoice rather than charging now — same split WizardStepFinish
   *  uses to decide between its receipt and its Upcoming Charge table. */
  const isUpcomingCharge = finishAction === 'increase' || finishAction === 'decreaseNotRefund';

  const planName = chargeResult?.planName;
  const screens = chargeResult?.screens ?? 0;
  const email = chargeResult?.email;
  const credit = chargeResult?.credit;

  /**
   * One money row, labelled for what actually happened: money taken now is "Paid today", money
   * that lands on the next invoice is "Upcoming charge" — the same wording the full receipt and
   * Payment History use for a not-yet-billed amount. ("Next charge" read as the recurring
   * renewal price rather than this one-off adjustment.)
   *
   * `upcoming.total` is in cents while `totalPaidToday` is already in dollars, hence the divide
   * on only one of them.
   */
  const upcoming = chargeResult?.upcoming;
  /**
   * The invoice path. When the change bills on the NEXT invoice we have that invoice, so every
   * figure below comes off it — and the block then reads exactly like `UpcomingChargeDialog`
   * (the same arithmetic, the same labels, the same shared helpers), because it is describing
   * the same document.
   *
   * #2160 — this block used to mix sources: the headline came off the invoice
   * (`upcoming.total`, tax-inclusive) while the closing "Upcoming payment" row came off
   * `chargeResult.total`, which is the pre-tax `clientSubtotal` and is never reconciled with the
   * backend. Both were on screen at once, so the dialog stated two different next-invoice
   * amounts and its own Subtotal + Tax did not add up to either. Deriving the whole block from
   * one document is what makes that class of contradiction impossible rather than merely fixed.
   */
  const fromInvoice = isUpcomingCharge && upcoming ? upcoming : null;

  /**
   * `amount_due` is what is actually owed after the customer's balance is applied; Stripe can
   * report it negative when the balance covers the invoice, which is a credit carried forward,
   * not money taken. Clamped at zero the same way `UpcomingChargeDialog` clamps it (#1162), so
   * the two never disagree on the same invoice.
   */
  const invoiceAmountDue = (() => {
    if (!fromInvoice) return null;
    const raw = fromInvoice.amount ?? fromInvoice.amount_due;
    if (typeof raw !== 'number') return null;
    return raw <= 0 ? 0 : raw / 100;
  })();

  /**
   * The headline: one figure, named for what it is. "Amount due" on the invoice path matches
   * control's Upcoming Charge dialog, which is where a customer sees this same number again.
   *
   * Falls back to the invoice total when Stripe omitted `amount_due` — worse than the real
   * figure, still tax-inclusive, and never the pre-tax subtotal.
   */
  const money = isUpcomingCharge
    ? /* Branches on the ACTION, not on whether an invoice arrived. These two bill on the next
         invoice, so with no invoice to quote the row is omitted entirely — falling through to
         `totalPaidToday` would put "Paid today" on a change that took no money today. */
      invoiceAmountDue != null
      ? { label: t('Amount due'), amount: invoiceAmountDue }
      : typeof fromInvoice?.total === 'number'
        ? { label: t('Amount due'), amount: Math.abs(fromInvoice.total) / 100 }
        : null
    : typeof chargeResult?.totalPaidToday === 'number'
      ? { label: t('Paid today'), amount: chargeResult.totalPaidToday }
      : null;

  /**
   * What the subscription IS, and — where it differs — what it was.
   *
   * Replaces a lone "Billing: Yearly" row. Stating only the end state meant the one thing the
   * reader came to check ("did my change actually take?") had to be reconstructed from memory;
   * a plan swap that kept the same screen count produced a dialog indistinguishable from a
   * screen-count change on the same plan.
   *
   * All three rows always render, changed or not: the unchanged ones are the context that makes
   * the changed one legible, and a row appearing and disappearing between runs is harder to
   * scan than a stable three-row block. `from` is set only when the value actually moved, so
   * the arrow marks exactly what the charge did.
   */
  /**
   * The money breakdown, in the order a receipt reads: what the plan costs, what tax was added,
   * what it settles at next period, what actually left the account today.
   *
   * This used to live only on the full-page `WizardStepFinish`, which the order-device-coupon
   * path still fell back to — so a purchase that earned a free device got a different (and much
   * heavier) receipt than one that did not. Same figures, rendered in this dialog's rows.
   */
  /**
   * Tax, from the invoice where we have one. `tax` is CENTS on both paths.
   *
   * #2162 — a zero is a number, so filtering on `typeof line?.tax === 'number'` alone rendered
   * `Sales Tax - Texas(8.25%) $0.00` as a second, contradictory-looking row beside the real one.
   * A jurisdiction that charged nothing is not a fact the receipt needs to state, so it is
   * dropped rather than printed at zero.
   */
  const taxLines = ((fromInvoice ? fromInvoice.taxs : chargeResult?.taxs) ?? []).filter(
    (line): line is { name?: string; tax: number } =>
      typeof line?.tax === 'number' && line.tax !== 0,
  );

  /**
   * What the money is FOR — one row per plan and per add-on.
   *
   * #2162 — the dialog previously named only the plan (`Plan: Standard`, $10.00) above a
   * Subtotal that included every add-on ($65.00), leaving 69% of the figure unexplained and the
   * receipt impossible to reconcile. Both `WizardStepFinish`es already itemise this; the compact
   * dialog dropped it, which was collateral rather than intent (its header comment rules out the
   * full invoice TABLE, not the add-on rows).
   *
   * Two sources, because the two paths have different documents available:
   *   - invoice path — `upcoming.lines`, aggregated by subscription item so Stripe's proration
   *     split (a $0 cleanup row + the real charge) collapses to one row per product (#1213).
   *   - paid-today path — the wizard's own `addonItems`, since there is no upcoming invoice yet.
   */
  const itemRows: Array<{ key: string; label: string; amount: number }> = fromInvoice
    ? aggregateInvoiceLines(fromInvoice.lines?.data ?? []).map((line, i) => ({
        key: `line-${i}`,
        label: shortenInvoiceDescription(line.description) || t('Subscription'),
        amount: (line.amount ?? 0) / 100,
      }))
    : (() => {
        const addons = (chargeResult?.addonItems ?? []).filter(
          (addon) => typeof addon?.total === 'number',
        );
        if (addons.length === 0) return [];
        const addonRows = addons.map((addon, i) => ({
          key: `addon-${i}`,
          label: addon.name ?? t('Add-on'),
          amount: addon.total ?? 0,
        }));
        /**
         * `addonItems` carries ONLY the add-ons — the plan's own money is in the subtotal but is
         * not a row in it. Listing the add-ons alone would put "$15.00" above a "$30.00" subtotal
         * and reproduce, on this path, exactly the unaccountable gap that #2162 is about.
         *
         * So the plan line is the remainder: subtotal minus what the add-ons account for. It is
         * derived rather than read because the wizard never builds a plan row, and derived from
         * the SAME subtotal that is displayed, so the column always sums to the figure beneath
         * it. Skipped when there is no subtotal to take a remainder of, or when the remainder is
         * not positive (a discounted or fully-credited plan) — better no row than a $0.00 or
         * negative one nobody can explain.
         */
        const addonTotal = addonRows.reduce((sum, row) => sum + row.amount, 0);
        const planAmount =
          typeof chargeResult?.subtotal === 'number' ? chargeResult.subtotal - addonTotal : 0;
        if (planAmount <= 0) return addonRows;
        return [
          {
            key: 'plan',
            label: chargeResult?.planName ?? t('Subscription'),
            amount: planAmount,
          },
          ...addonRows,
        ];
      })();

  /** Dollars on both paths. The invoice's subtotal is pre-tax and pre-discount, like the client's. */
  /**
   * A SINGLE line item is dropped; two or more are kept.
   *
   * With one product, the row only restates what the Screens row now carries — count, product and
   * unit price — at a Subtotal identical to its own amount, so it is a line saying the same thing
   * as the line above and the line below.
   *
   * The moment there is more than one, that stops being true: the add-ons are money in the
   * Subtotal that nothing else on the dialog names, which is #2162 exactly. So the threshold is
   * the rule, not a preference — never collapse a multi-item breakdown.
   */
  const visibleItemRows = itemRows.length > 1 ? itemRows : [];

  const subtotal = fromInvoice
    ? typeof fromInvoice.subtotal === 'number'
      ? fromInvoice.subtotal / 100
      : undefined
    : chargeResult?.subtotal;

  /** Invoice-level coupons, stacked into one row — same treatment as the other three surfaces (#1220). */
  const discountAmount = fromInvoice ? sumDiscountAmounts(fromInvoice.total_discount_amounts) : 0;
  const discountLabel = fromInvoice ? formatDiscountLabel(fromInvoice.discount?.coupon) : '';

  /** The tax-inclusive total of the lines above it — the row that `chargeResult.total` was wrongly filling. */
  const invoiceTotal =
    fromInvoice && typeof fromInvoice.total === 'number' ? fromInvoice.total / 100 : undefined;

  /**
   * Customer balance applied to this invoice, derived exactly as `UpcomingChargeDialog` derives
   * it, so the same invoice reports the same figure in both places. Rendered only when it moved:
   * an unconditional "$0.00 applied" row reads as something having happened when nothing did.
   */
  const appliedBalance = (() => {
    if (!fromInvoice) return 0;
    const applied =
      -((fromInvoice.ending_balance ?? 0) - (fromInvoice.starting_balance ?? 0)) / 100;
    /**
     * Normalise NEGATIVE ZERO. The negation above turns the common "no balance moved" case into
     * `-0`, which `Intl.NumberFormat` faithfully renders as "-$0.00" — a minus sign on nothing,
     * on the one row whose whole job is to reassure the reader that nothing was applied.
     * `-0 === 0` is true, so this comparison catches it without a special-case helper.
     */
    return applied === 0 ? 0 : applied;
  })();
  /**
   * The date the upcoming invoice is issued — i.e. when this money is actually taken.
   *
   * `formatWithPattern` (dayjs, shared) rather than a bare `new Date(...)`: a malformed value
   * renders the fallback instead of crashing a confirmation the user has just paid for. Pattern
   * matches control's `MM/DD/YYYY`.
   */
  const upcomingChargeDate = fromInvoice?.created
    ? formatWithPattern(new Date(fromInvoice.created * 1000).toISOString(), 'MM/DD/YYYY', '')
    : '';
  /** The coupon CTA needs both halves to build its link — see the type. */
  const orderDeviceLink =
    chargeResult?.orderDeviceCoupon && chargeResult?.orderDeviceCouponNumber
      ? `/app/s/order-device?coupon=${chargeResult.orderDeviceCoupon}&couponNumber=${chargeResult.orderDeviceCouponNumber}&source=subscription`
      : null;

  /**
   * "$108.00/yr each" — the per-screen price, abbreviated because it sits inside a row label
   * rather than standing on its own.
   *
   * The period comes from `billingCycle`, which is a DISPLAY label ('Monthly' / 'Yearly') built by
   * the wizard from `BILLING_INTERVAL_LABELS`, so it is matched case-insensitively against the
   * English rather than compared to an enum. Anything unrecognised drops the period instead of
   * guessing — quoting a yearly price as monthly is a worse error than quoting no period at all.
   */
  const perScreenPrice = (() => {
    const price = chargeResult?.pricePerScreen;
    if (typeof price !== 'number' || price <= 0) return null;
    const amount = formatCurrencyAmount(price, currency);
    const cycle = (chargeResult?.billingCycle ?? '').toLowerCase();
    if (cycle.startsWith('year')) return t('{{amount}}/yr each', { amount });
    if (cycle.startsWith('month')) return t('{{amount}}/mo each', { amount });
    return t('{{amount}} each', { amount });
  })();

  const rows: Array<{ key: string; label: string; from?: string; to: string }> = [];
  const changedFrom = (previous: string | undefined, next: string) =>
    !isFirstSubscribe && previous && previous !== next ? previous : undefined;
  if (planName) {
    rows.push({
      key: 'plan',
      label: t('Plan'),
      from: changedFrom(chargeResult?.previousPlanName, planName),
      to: planName,
    });
  }
  if (screens > 0) {
    const previousScreens = chargeResult?.previousScreens;
    rows.push({
      key: 'screens',
      /**
       * The unit price rides on this row's LABEL, not in a line item of its own. "Screens 1 → 2"
       * and "2 × Standard Yearly (at $108.00 / year)" were the same fact told twice — count,
       * product and unit price spread over two rows. Putting the price beside the count leaves
       * one row that answers "how many, at what each", and lets the single-product line item go.
       */
      label: perScreenPrice ? `${t('Screens')} (${perScreenPrice})` : t('Screens'),
      from:
        previousScreens != null && previousScreens > 0
          ? changedFrom(String(previousScreens), String(screens))
          : undefined,
      to: String(screens),
    });
  }
  if (chargeResult?.billingCycle) {
    rows.push({
      key: 'billing',
      label: t('Billing'),
      from: changedFrom(chargeResult.previousBillingCycle, chargeResult.billingCycle),
      to: chargeResult.billingCycle,
    });
  }

  return (
    <>
      {/* A plain wrapper, NOT `DialogHeader`. DialogHeader sets `hasHeaderCloseButton` on the
            dialog's context, which suppresses DialogContent's absolute close button and renders an
            inline X at the right edge of the header row instead. That is right for a single-column
            dialog and wrong here: with the header inside the LEFT column, the X landed against the
            divider in the middle of the popup rather than at its top-right corner. Skipping
            DialogHeader leaves the flag false, so DialogContent draws its own X at `right-4 top-4`
            — the corner of the whole dialog, which is where a close control belongs.
            DialogTitle and DialogDescription stay: they are what Radix announces, and they are
            independent of the header wrapper. */}
      <div className="space-y-2 pb-2">
        {/* Icon inline with the title, not a large centred badge above it — see the component
            rules on dialog headers. */}
        {/* Says what just happened, in both cases. This used to greet a first subscriber with
            "Welcome to OptiSigns!", which named the wrong event: the person has been in the
            product through a whole trial, so being welcomed on the way OUT of the paywall read as
            a greeting for something they had already been doing. It also needed a white-label
            variant, since a white-label portal must not say "OptiSigns". Naming the outcome
            instead is truer to the moment and needs no branding branch. Deliberately does NOT
            name the tier ("You're on Pro Plus") — the Plan row below already states it, with the
            before-value on a change. */}
        {/* No icon. The title already says the thing succeeded, and a green tick beside it says
            it a second time in a glyph — while pulling the eye to the smallest element in the
            largest line. Nothing else in this dialog is iconified either. */}
        {/* Just the thanks on a first subscribe: the description directly below already says
            "You've started your subscription", so naming it in the title too spent the dialog's
            largest line on a sentence it repeats a line later.
            A change keeps its outcome instead — thanking someone for a quantity change would read
            as thanking them for filling in a form. Neither string carries branding, which is why
            "Welcome to OptiSigns!" was dropped: that one needed a white-label variant. */}
        <DialogTitle className="text-xl font-semibold">
          {isFirstSubscribe ? t('Thank you!') : t('Your plan is updated')}
        </DialogTitle>
        {/* Confirmation plus where the paperwork went, in one line — NOT a restatement of the
            subscription. This used to read "{plan} is now active on {n} screens", which the
            Plan and Screens rows below now say more precisely (and with the before-values),
            so the subheader was spending the most prominent line in the dialog on a duplicate.
            Folding the email in here is also why there is no standalone receipt paragraph
            further down. */}
        <DialogDescription>
          {email
            ? isFirstSubscribe
              ? t("You've started your subscription. We emailed the details to {{email}}.", {
                  email,
                })
              : t('Your changes are saved. We emailed the details to {{email}}.', { email })
            : isFirstSubscribe
              ? t("You've started your subscription.")
              : t('Your changes are saved.')}
        </DialogDescription>
      </div>

      {/* Only the facts someone checks on a confirmation: what the subscription now is, then the
          money — derived top to bottom and ending in the one figure that matters. */}
      <div className="space-y-3 py-2 text-sm">
        {/* The headline figure USED to sit here, above the subscription rows, with the
            breakdown's own closing row repeating it further down. Two statements of the same
            number, in two places, under two labels — which is the shape #2160 was reported as.
            It now lives at the FOOT of the breakdown instead, where the lines above add up to
            it, so the dialog states it once and shows its derivation. */}
        {/* The account-credit row moved with it: on the invoice path the invoice's own
            "Applied balance" says the same thing more precisely, and on the paid-today path the
            credit belongs beside the figure it reduced, not above the plan. */}
        {rows.map((row) => (
          <div
            key={row.key}
            className="flex items-center justify-between gap-4 px-3"
            data-testid={`subscribed-row-${row.key}`}
          >
            <span className="text-muted-foreground">{row.label}</span>
            {/* The old value stays readable rather than struck through: it is being reported,
                not cancelled. Weight carries the difference — the new value is the semibold
                one, so a changed row reads left-to-right as "was this, is now that". */}
            <span className="text-right">
              {row.from && (
                <>
                  <span className="text-muted-foreground">{row.from}</span>
                  <span aria-hidden="true" className="mx-1.5 text-muted-foreground">
                    &rarr;
                  </span>
                </>
              )}
              <span className={row.from ? 'font-semibold text-foreground' : 'text-foreground'}>
                {row.to}
              </span>
            </span>
          </div>
        ))}

        {/* The money breakdown, under the subscription rows and separated from them by a rule:
            what changed is one statement, what it cost is another. Each row is conditional — see
            the type — so a receipt that only carries a total shows exactly one line rather than a
            column of zeroes, which is what made the old full-page version hard to read. */}
        {(typeof subtotal === 'number' ||
          visibleItemRows.length > 0 ||
          taxLines.length > 0 ||
          typeof invoiceTotal === 'number' ||
          money) && (
          <div className="space-y-3 border-t pt-3">
            {/* What is being billed, before any of the arithmetic on it. Sits above Subtotal for
                the same reason control's table does: the reader adds these up and arrives at the
                next line.
                A LONE item is not rendered — see `visibleItemRows`. */}
            {visibleItemRows.map((item) => (
              <div
                key={item.key}
                className="flex items-center justify-between gap-4 px-3"
                data-testid="subscribed-line-item"
              >
                <span className="min-w-0 text-muted-foreground">{item.label}</span>
                <span className="shrink-0 text-foreground">
                  {formatCurrencyAmount(item.amount, currency)}
                </span>
              </div>
            ))}
            {typeof subtotal === 'number' && (
              <div
                className="flex items-center justify-between px-3"
                data-testid="subscribed-subtotal"
              >
                <span className="text-muted-foreground">{t('Subtotal')}</span>
                <span className="text-foreground">{formatCurrencyAmount(subtotal, currency)}</span>
              </div>
            )}
            {/* Between Subtotal and tax, matching the other three money surfaces. */}
            {discountAmount > 0 && (
              <div
                className="flex items-center justify-between gap-4 px-3"
                data-testid="subscribed-discount"
              >
                {/* Coupon name appended OUTSIDE the translation, exactly as
                    `UpcomingChargeDialog` does it — the name is Stripe data, not copy. */}
                <span className="min-w-0 text-muted-foreground">
                  {t('Discount')}
                  {discountLabel ? ` - ${discountLabel}` : ''}
                </span>
                <span className="shrink-0 text-primary">
                  -{formatCurrencyAmount(discountAmount, currency)}
                </span>
              </div>
            )}
            {/* One row per jurisdiction, and `name` already reads "Sales Tax - Texas(8.25%)" —
                built by the backend, so the percentage stays correct without this dialog knowing
                any tax rules. `tax` is CENTS here while every other figure is dollars. */}
            {taxLines.map((line, i) => (
              <div
                key={`${line.name ?? 'tax'}-${i}`}
                className="flex items-center justify-between gap-4 px-3"
                data-testid="subscribed-tax"
              >
                <span className="min-w-0 text-muted-foreground">{line.name ?? t('Sales Tax')}</span>
                <span className="shrink-0 text-foreground">
                  {formatCurrencyAmount(line.tax / 100, currency)}
                </span>
              </div>
            ))}
            {/* The total OF THE LINES ABOVE — Subtotal, less any discount, plus tax.
                This replaces the "Upcoming payment" row, which sat in the total position while
                restating the pre-tax subtotal (#2160). Anything in this slot has to be the sum
                of what precedes it; that is the whole job of the position. */}
            {typeof invoiceTotal === 'number' && (
              <div
                className="flex items-center justify-between px-3"
                data-testid="subscribed-next-bill"
              >
                <span className="text-muted-foreground">{t('Total')}</span>
                <span className="text-foreground">
                  {formatCurrencyAmount(invoiceTotal, currency)}
                </span>
              </div>
            )}
            {/* Unconditional on the invoice path, unlike every other row here, because control
                prints it at $0.00 too: between Total and Amount due, two figures that are equal
                whenever nothing was applied, a missing row is the reader asking "why are these
                the same?". The zero IS the explanation. Suppressed off the invoice path, where
                there is no invoice balance to report and `Account credit applied` covers it. */}
            {fromInvoice && (
              <div
                className="flex items-center justify-between px-3"
                data-testid="subscribed-applied-balance"
              >
                <span className="text-muted-foreground">{t('Applied balance')}</span>
                <span className={appliedBalance !== 0 ? 'text-primary' : 'text-foreground'}>
                  {formatCurrencyAmount(appliedBalance, currency)}
                </span>
              </div>
            )}
            {/* Account credit on the paid-today path, where there is no invoice balance row.
                Only when there IS one: an unconditional "$0.00 credit" reads as something having
                happened when nothing did. */}
            {!fromInvoice && typeof credit === 'number' && credit !== 0 && (
              <div
                className="flex items-center justify-between px-3"
                data-testid="subscribed-credit"
              >
                <span className="text-muted-foreground">{t('Account credit applied')}</span>
                <span className="font-medium text-primary">
                  {formatCurrencyAmount(credit, currency)}
                </span>
              </div>
            )}
            {/* THE figure, last and weighted — the end of the derivation rather than a claim made
                before it. Everything above it is the arithmetic that produces it, which is the
                whole reason it moved down here from the top of the dialog.
                The date rides on THIS row rather than on a heading of its own: "Amount due" and
                "Upcoming Charge <date>" were two labels for one fact, and the row that states the
                figure is the row that should say when it is taken. Without a date here, a
                next-invoice amount sitting under "Your plan is updated" reads as money moving now.
                `upcoming.created` is the same field the full-page `WizardStepFinish` dates its
                table from, so the two surfaces never name different days for one charge. */}
            {money && (
              <div className="flex items-baseline justify-between gap-4 rounded-md bg-muted px-3 py-2">
                <span className="min-w-0 font-medium text-foreground">
                  {money.label}
                  {upcomingChargeDate && (
                    <span
                      className="ml-2 font-normal text-muted-foreground"
                      data-testid="subscribed-charge-date"
                    >
                      {upcomingChargeDate}
                    </span>
                  )}
                </span>
                <span
                  className="shrink-0 font-semibold text-foreground"
                  data-testid="subscribed-amount"
                >
                  {formatCurrencyAmount(money.amount, currency)}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Hardware promotion. The moment someone commits to a subscription is the moment they need
          something to run it on, and this dialog was previously a dead end — the only exit was the
          X. A quiet bordered row rather than a second primary button, so it reads as an offer
          alongside the receipt and not as the action the dialog is waiting on.
          Destination is `useHardwareCta`, shared with the welcome screen: a root admin
          in the default team gets the in-app Order Devices page (billed to the account), everyone
          else gets the storefront, because `/app/s/order-device` renders nothing but "ask your
          Admin" outside those conditions (#1904). Reusing the hook is what stops that rule
          drifting from the other entry points.
          Hidden on white-label — see the flag's note above. */}
      {/* Hardware promotion — its own section under a rule, not a column beside the receipt.
          Two things are being separated here: what just happened to your subscription, and an
          unrelated offer. Side by side they read as one object and the offer competed with the
          receipt for first glance; stacked under a divider, the receipt is finished before the
          offer starts.

          FIRST SUBSCRIBE only. The moment someone starts a subscription is the moment they need
          something to run it on. A plan change is not that moment — an existing subscriber already
          has hardware, so offering them a device next to a receipt for a screen-count change is
          noise on a confirmation they only opened to check a number. Hence `hasHardwarePromo`
          rather than showing on both paths.

          Destination is `useHardwareCta`, shared with the welcome screen: a root admin
          in the default team gets the in-app Order Devices page (billed to the account), everyone
          else gets the storefront, because `/app/s/order-device` renders nothing but "ask your
          Admin" outside those conditions (#1904). Reusing the hook is what stops that rule
          drifting from the other entry points.
          Hidden on white-label — see the flag's note above. */}
      {/* The free device this purchase EARNED, when it earned one. Distinct from the generic
          hardware promo below and it wins over it: an offer to buy a device makes no sense beside
          an unclaimed free one. This CTA is why the coupon path used to fall back to the old
          full-page receipt — it lived only there, so bringing it here is what lets both paths
          share this dialog.
          A link, not a Button+navigate: the dialog is unmounted by the route change either way,
          and an anchor gets middle-click and "open in new tab" for free. */}
      {orderDeviceLink && !isWhiteLabel && (
        <div className="border-t pt-4" data-testid="subscribed-free-device">
          <Button asChild className="w-full">
            <a href={orderDeviceLink}>{t('Order Your Free Device')}</a>
          </Button>
        </div>
      )}

      {!orderDeviceLink && hasHardwarePromo(chargeResult, isWhiteLabel) && (
        <div
          /* Full-width tinted band at the foot of the receipt: copy and action on the left, the
             device bleeding off the right edge. Same `media-gradient-*` tokens the trial-expired
             dialog's artwork panel uses, so the two billing surfaces share one palette — and they
             are theme-aware, which a hardcoded tint would not be.
             Runs `to-r`, colour on the left fading right — but over a NARROWED range, because the
             full one was too strong at the left edge and washed out to nothing at the right.
             Two adjustments, one per end:
               - starts at the `via` stop (#93e7d5), not `from` (#4ec5ac), so the left is a soft
                 mint rather than the saturated one;
               - `to-[180%]` pushes the pale stop (#f3fffc) well past the right edge, so the band
                 only ever reaches ~56% of the way there. The visible right edge lands around
                 #c8f4eb — still clearly tinted instead of fading to near-white.
             Both stops are tokens, so this holds in dark mode too (the same 56% of #2b5f57 to
             #17202a). Widen the range by lowering the 180%, or deepen the left by swapping `via`
             back to `from`.
             `mt-5` (20px), not `mt-2`: the receipt above already ends with its own `py-2`, so this
             clears the last row by 28px. At `mt-2` the total was 16px and the band read as another
             row of the breakdown rather than a separate offer below it. */
          className="mt-5 overflow-hidden rounded-lg bg-gradient-to-r from-media-gradient-via to-media-gradient-to to-[180%]"
          data-testid="subscribed-hardware-promo"
        >
          {/* `items-start`, so the copy sits at the TOP of the band rather than centred in it.
              The image side then needs `self-stretch` to keep filling the full height (see
              below) — without it, top-aligning the row would drag the device up with the copy. */}
          <div className="flex items-start gap-3">
            {/* Text column takes the slack (`flex-1`), which is what makes the heading WRAP to two
                lines instead of running the band's full width on one. The image side is a fixed
                column now, so the old problem this once caused — empty space pooling here and
                shoving the device against the edge — no longer applies: the device's column is a
                known width and the circle is pinned to the band's right edge independently.
                `min-w-0` lets the heading wrap rather than overflow when a translation runs long. */}
            <div className="flex min-w-0 flex-1 flex-col items-start gap-3 py-4 pl-4">
              <p className="text-base font-semibold text-foreground">
                {t('Need a device to run your screens?')}
              </p>
              {/* `bg-card` on an outline button: a transparent outline on a tinted band reads as
                  disabled. */}
              <Button
                variant="outline"
                size="sm"
                onClick={openHardwareCta}
                className="bg-card"
                data-testid="subscribed-order-devices-btn"
              >
                {t('Order Devices')}
              </Button>
            </div>
            {/* OptiStick's GALLERY HERO (`00.png`), not the `thumb.png` the product strip uses for
                its tile — see the note on `PRODUCT_IMAGES` in ProductShowcaseStep, where rank 0 is
                the one product whose tile is a different shot from its hero.
                `flex-1` on the wrapper with `justify-center` is what centres the device in whatever
                width the copy leaves over, instead of pinning it to the right edge.
                `object-contain`, not `cover`: the source is a transparent cut-out of the device, so
                cover would crop the product rather than filling a photo. The box is `h-36 w-36`
                and the source is aspect 1.23, so contain fits it to the WIDTH — 144x117 drawn
                inside a 144x144 box, which is why there is a little gradient above and below it.
                The two dimensions do different jobs: `h-36` sets the BAND's height (the text
                column is only ~96px, so the image is the taller of the two), and `w-36` sets the
                DEVICE's size. Changing only the height adds empty gradient rather than a bigger
                product shot.
                Fixed box so a slow CDN response cannot reflow the dialog. Decorative: the copy
                beside it names the offer, so an alt string would be a second voice saying the same
                thing to a screen reader. */}
            <div className="relative flex w-40 shrink-0 items-center justify-center self-stretch">
              {/* Deeper-tint disc sitting UNDER the device, anchored to the band's right edge and
                  overhanging it: `-right-12` puts 48px of the circle outside, and at `h-52 w-52`
                  (208px) it also stands taller than the 144px band. The band's own
                  `overflow-hidden` does the cutting on all three edges, so it reads as a disc
                  passing behind the product rather than a badge floating on it.
                  `bg-media-gradient-from` is the saturated end of the same palette the band runs
                  between its two lighter stops, so this is a deeper shade of the surface it sits on
                  rather than a new colour — and it stays theme-aware. At /40 it separates from the
                  gradient without competing with the copy on the left.
                  Anchored to the RIGHT rather than centred, so the amount cut off is fixed by the
                  band's edge and does not drift as the copy rewraps. The numbers line up: at
                  `w-52` (208px) less the `-right-12` (48px) overhang, exactly 160px of disc stays
                  visible — the same width as this `w-40` column — so the device, centred in the
                  column at `w-36` (144px), lands centred on the visible disc with the whole
                  product in view. Knobs: `-right-12` for how much is cut, `h-52 w-52` for the
                  disc's size; if the column width changes, keep `w-52 - right-offset` equal to
                  it. */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -right-12 top-1/2 h-52 w-52 -translate-y-1/2 rounded-full bg-media-gradient-from/40"
              />
              <img
                src={`${PRODUCT_IMAGE_BASE}/optistick/00.png`}
                alt=""
                aria-hidden="true"
                loading="lazy"
                className="relative h-36 w-36 object-contain"
              />
            </div>
          </div>
        </div>
      )}

      {/* No footer button on EITHER path now. After a successful charge there is nothing to
          confirm, retry or cancel, so a button that does exactly what the header's X already does
          was a second control for one outcome — and it made a receipt look like a form awaiting
          submission. Both exits run the same `handleOpenChange(false)`, which is what performs the
          post-subscribe navigation, so nothing is lost by leaving it out.
          The `onContinue` prop went with the button: `handleOpenChange` is wired to the `Dialog`
          itself, so the X already performs the navigation without this component's help. */}
    </>
  );
}
